import { configureStore, createSlice, createAction } from '@reduxjs/toolkit';
import { setAutoFreeze } from 'immer';
import { SLICE_DEFS, WORKSTATE_SLICES } from './fields';

// The original state lived in useState(), which never freezes stored objects and lets consumers
// mutate them in place. Redux Toolkit uses Immer, which auto-freezes state in development. To keep
// behavior byte-for-byte identical during this shim migration (some consumers mutate stored objects
// directly), we disable Immer auto-freeze and RTK's dev-only serializable/immutable checks.
// This is intentional for the Phase 2 shim; Phase 3 can re-enable checks slice-by-slice.
setAutoFreeze(false);

// Build a fresh copy of a slice's initial state. All object/array initials are empty, so this
// reproduces them as brand-new empties (never sharing a reference across resets), while primitives
// pass through unchanged. Used when handling resetWorkState to restore a slice to pristine values.
function freshInitial(template) {
  const out = {};
  for (const k in template) {
    const v = template[k];
    out[k] = Array.isArray(v) ? [] : (v && typeof v === 'object') ? {} : v;
  }
  return out;
}

// One action meaning "a fresh visit to the WorkDetail subtree begins/ends". Every gallery-scoped
// slice handles it by returning pristine initial values; app-lifetime slices simply do not handle
// it, so their state cannot be cleared by navigating in and out of the gallery. One dispatch means
// one store notification, instead of one per slice.
export const resetWorkState = createAction('workDetail/resetState');

// Generate one slice per domain group. Each slice exposes `setField({key,value})`, which the shims
// map 1:1 to the original setX(...) setters.
const reducers = {};
const setFieldActions = {};

Object.entries(SLICE_DEFS).forEach(([name, initialState]) => {
  const slice = createSlice({
    name,
    initialState,
    reducers: {
      setField: (state, action) => {
        const { key, value } = action.payload;
        state[key] = value;
      },
    },
    extraReducers: (builder) => {
      if (WORKSTATE_SLICES.includes(name)) {
        builder.addCase(resetWorkState, () => freshInitial(initialState));
      }
    },
  });
  reducers[name] = slice.reducer;
  setFieldActions[name] = slice.actions.setField;
});

export const SET_FIELD = setFieldActions;

export const store = configureStore({
  reducer: reducers,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }),
});

export default store;
